/** @jsx isolateComponent */
import { css } from '@emotion/react';
import { Animation, Colors, Link, Typography } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { useUniversalNavContext } from '../../../hooks/useUniversalNavContext';
import type { UniversalNavLinkType } from '../../../constants';
import { emitUserInteractionEvent } from '../../../utils/avroEvents';
import { ArrowLinkIcon } from '../ArrowLinkIcon';
import type { AnchorTarget } from '@zapier/design-system';

type Props = {
  /**
   * List of links in the MegaNav
   */
  submenu: UniversalNavLinkType[];
  /**
   * Function called when closing menu on a link from within the MegaMenu
   */
  onCloseMenu?: () => void;
};

const Styles = labelStyles('SubMenuList', {
  root: css({
    display: 'flex',
    flexDirection: 'column',
  }),
  item: {
    marginBottom: '10px',
    ...Typography.paragraph3,
    '&:last-child': {
      marginBottom: 0,
    },
    '&:focus-within': {
      outline: `1px solid ${Colors.blue}`,
    },
  },
  link: css({
    color: Colors.neutral700,
    ...Typography.paragraph3,
    position: 'relative',
    textDecoration: 'none',
    transition: `text-decoration 100ms ${Animation.transitionTimingFunction}`,
    '&:hover': {
      textDecoration: 'underline',
    },
    '&:hover > span': {
      marginLeft: 4,
      opacity: 1,
    },
    '&:focus': {
      outline: 'none',
    },
  }),
});

/**
 * @private
 * List of links inside the MegaMenu
 */
export const SubMenuList = (props: Props) => {
  const { submenu } = props;
  const { getFullyQualifiedURL } = useUniversalNavContext();

  const handleEvent = (text: string, eventType: string) => {
    emitUserInteractionEvent({
      event_action: eventType,
      option_selected: `MegaMenuSubmenuLink: ${text}`,
    });
    if (eventType === 'click' && props.onCloseMenu) props.onCloseMenu();
  };

  return (
    <ul css={Styles.root}>
      {submenu.map((link, index) => (
        <li css={Styles.item} key={`${link.text}-${index}`}>
          <Link
            css={Styles.link}
            color={null}
            href={getFullyQualifiedURL(link.href) || undefined}
            onClick={() => handleEvent(link.text, 'click')}
            onMouseEnter={() => handleEvent(link.text, 'hover')}
            target={link.target as AnchorTarget}
          >
            {link.text}
            <ArrowLinkIcon />
          </Link>
        </li>
      ))}
    </ul>
  );
};
